import {
  WSActions,
  WSAlert,
  WSCard,
  WSForm,
  WSInfoBox,
  WSInputText,
  WSList,
  WSSectionToolbar,
  WSSelect
} from "@wingspanhq/fe-component-library";
import React, { useMemo, useState } from "react";
import { UseFormMethods } from "react-hook-form";
import { ErrorAlert } from "@components/ErrorAlert";
import { COUNTRY_OPTIONS } from "../../../constants/user";
import { useQueryFeatureFlags } from "@hooks/useQueryFeatureFlags";
import {
  useQueryAccountRequirements,
  useQueryAvailableCurrenciesForCountry
} from "../../../query/users/queries";
import { IPSupport } from "./IPSupport";
import { FormData } from "./types";
import { getValidationSchema } from "./validationSchema";

type Props = {
  onSubmit: (data: FormData) => void;
  error?: any;
  isLoading?: boolean;
  defaultValues?: Partial<FormData>;
  onBack?: () => void;
  withPanel?: boolean;
  localCurrencyOnly?: boolean;
  fallbackCurrency?: string;
  lockCountry?: boolean;
};

export const Form: React.FC<Props> = ({
  onSubmit,
  error,
  isLoading,
  defaultValues,
  onBack,
  withPanel,
  localCurrencyOnly,
  fallbackCurrency,
  lockCountry
}) => {
  const featureFlags = useQueryFeatureFlags();
  const [country, setCountry] = useState<string>(defaultValues?.country || "");
  const [currency, setCurrency] = useState<string>(
    defaultValues?.currency || ""
  );

  const queryAvailableCurrenciesForCountry =
    useQueryAvailableCurrenciesForCountry(
      {
        country
      },
      {
        enabled: !!country
      }
    );

  const currencyOptions = useMemo(() => {
    const all = queryAvailableCurrenciesForCountry.data?.currencies || [];

    const filtered = (() => {
      if (!localCurrencyOnly) return all;
      const local = all.filter(c => c.corridors.includes("LOCAL"));
      if (local.length > 0) return local;
      // No local corridor: prefer the integrator's fallback currency if the
      // SWIFT list offers it, otherwise show the full SWIFT list.
      const swift = all.filter(c => c.corridors.includes("SWIFT"));
      if (fallbackCurrency) {
        const fallback = swift.filter(c => c.currency === fallbackCurrency);
        if (fallback.length > 0) return fallback;
      }
      return swift;
    })();

    return filtered.map(c => ({
      value: c.currency,
      label: c.currency
    }));
  }, [
    queryAvailableCurrenciesForCountry.data,
    localCurrencyOnly,
    fallbackCurrency
  ]);

  const queryAccountRequirements = useQueryAccountRequirements(
    {
      country,
      currency
    },
    {
      enabled: !!country && !!currency
    }
  );

  const accountRequirements = queryAccountRequirements.data;

  const countryCurrencyMismatch = useMemo(
    () =>
      queryAccountRequirements.error?.response?.data.error ===
      "No supported corridors found",
    [queryAccountRequirements.error?.response?.data.error]
  );

  const getPanelContent = (setValue: UseFormMethods<FormData>["setValue"]) => (
    <>
      <WSForm.Field
        name="country"
        label="Account country"
        component={WSSelect}
        componentProps={{
          options: COUNTRY_OPTIONS,
          internalSearch: true,
          status: countryCurrencyMismatch ? "error" : undefined,
          message: countryCurrencyMismatch
            ? "Country and account currency pair is not possible"
            : undefined,
          disabled: lockCountry
        }}
        onChange={value => {
          setCountry(value);
          // Unset currency any time country changes
          setValue("currency", null);
          setCurrency("");
        }}
      />

      <WSForm.Field
        name="currency"
        label="Account currency"
        component={WSSelect}
        componentProps={{
          disabled: !country,
          options: currencyOptions,
          internalSearch: true,
          // Currency codes are ISO identifiers and must not be auto-translated
          // by the browser (e.g. Google Translate turning "CHF" into
          // "congestive heart failure"), which makes the options unfindable. See PLA-3018.
          noTranslate: true,
          status: countryCurrencyMismatch
            ? "error"
            : queryAvailableCurrenciesForCountry.isFetching ||
                queryAccountRequirements.isFetching
              ? "loading"
              : undefined,
          message: countryCurrencyMismatch
            ? "Country and account currency pair is not possible"
            : undefined,
          isListLoading: queryAvailableCurrenciesForCountry.isFetching
        }}
        onChange={value => {
          setCurrency(value);
        }}
      />

      <WSForm.Value name="currency">
        {currencyValue => {
          const selected =
            queryAvailableCurrenciesForCountry.data?.currencies.find(
              c => c.currency === currencyValue
            );
          const isSwiftOnly =
            selected?.corridors.length === 1 &&
            selected.corridors[0] === "SWIFT";
          return isSwiftOnly ? (
            <WSInfoBox title="International wire transfer fees may apply">
              Please contact your bank for details on international wire
              transfer fees.
            </WSInfoBox>
          ) : null;
        }}
      </WSForm.Value>

      <WSForm.Field
        name="accountNumber"
        label={accountRequirements?.accountNumber.name || "Account Number"}
        component={WSInputText}
        componentProps={{
          fsExclude: true
        }}
      />

      <WSForm.Field
        name="confirmAccountNumber"
        label={
          accountRequirements?.accountNumber.name
            ? `Confirm ${accountRequirements.accountNumber.name}`
            : "Confirm Account Number"
        }
        component={WSInputText}
        componentProps={{
          fsExclude: true
        }}
      />

      {accountRequirements?.routing && (
        <WSForm.Field
          name="routing"
          label={accountRequirements?.routing.name || "Bank identifier"}
          component={WSInputText}
          componentProps={{
            fsExclude: true
          }}
        />
      )}

      {accountRequirements?.routingSecondary && (
        <WSForm.Field
          name="routingSecondary"
          label={accountRequirements.routingSecondary.name}
          component={WSInputText}
          componentProps={{
            fsExclude: true
          }}
        />
      )}

      {accountRequirements?.beneficiaryBankAccountType && (
        <WSForm.Field
          name="beneficiaryBankAccountType"
          label={accountRequirements.beneficiaryBankAccountType.name}
          component={WSSelect}
          componentProps={{
            options: accountRequirements.beneficiaryBankAccountType.options.map(
              option => ({ value: option, label: option })
            )
          }}
        />
      )}

      {accountRequirements?.beneficiaryNameLocal && (
        <WSForm.Field
          name="beneficiaryNameLocal"
          label={accountRequirements.beneficiaryNameLocal.name}
          component={WSInputText}
        />
      )}

      <ErrorAlert context="PaymentsSetup" error={error} />
    </>
  );

  return (
    <WSForm<FormData>
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      validationSchema={getValidationSchema(accountRequirements)}
    >
      {formContext => (
        <WSList gap="2XL">
          {!withPanel && (
            <WSSectionToolbar title="Enter your payout account details" />
          )}

          {withPanel ? (
            <WSCard header="Enter your payout account details">
              <WSList gap="XL">{getPanelContent(formContext.setValue)}</WSList>
            </WSCard>
          ) : (
            getPanelContent(formContext.setValue)
          )}

          {featureFlags.data?.internationalIP && (
            <WSForm.Values names={["country", "currency"]}>
              {({ country, currency }) =>
                country === "CA" && currency === "CAD" ? <IPSupport /> : null
              }
            </WSForm.Values>
          )}

          {countryCurrencyMismatch && (
            <WSAlert theme="error" icon="alert" title="Incompatible selections">
              The country and account currency pair is not possible. Please
              update the selection(s) and try again.
            </WSAlert>
          )}

          <WSActions
            alignment="fill"
            buttons={[
              {
                label: "Back",
                kind: "Secondary",
                onClick: onBack,
                visible: !!onBack,
                type: "button"
              },
              {
                label: "Continue",
                type: "submit",
                loading: isLoading,
                disabled:
                  countryCurrencyMismatch ||
                  queryAccountRequirements.isFetching ||
                  queryAvailableCurrenciesForCountry.isFetching
              }
            ]}
          />
        </WSList>
      )}
    </WSForm>
  );
};
